import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { pageMarkup } from './generated-page';
import ProductModulePage, { getModulePageByPath, modulePages } from './pages/ProductModulePage';

const DataDeletion = lazy(() => import('./pages/DataDeletion'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Support = lazy(() => import('./pages/Support'));

const modules = {
  resident: {
    kicker: 'Better estate living',
    title: 'The everyday estate app residents will actually use.',
    description: 'Visitors, school runs, service requests, permissions, payments and estate communication—available without chasing the estate office.',
    features: [
      { title: 'Visitor pre-approval', description: 'Pre-authorise guests, deliveries and service providers for faster, safer entry.' },
      { title: 'School-run notifications', description: 'Know when children leave for school, return home or are checked in by an authorised person.' },
      { title: 'Service requests', description: 'Submit maintenance and service requests, then follow their progress through resolution.' },
      { title: 'Bills, receipts and estate notices', description: 'View estate invoices, payment receipts, documents and official notices in one place.' },
      { title: 'Permissions management', description: 'Approve movement of children, vehicles, household assets and service providers when required.' },
      { title: 'Real-time alerts and updates', description: 'Get immediate alerts about visitors, incidents, requests, payments and important announcements.' }
    ]
  },
  estate: {
    kicker: 'Estate Administration',
    title: 'One reliable record for every unit, occupant and estate activity.',
    description: 'Manage property records, occupants, incidents and estate communication from one organised source of truth.',
    features: [
      { title: 'Incident management', description: 'Record, assign and track estate incidents through resolution.' },
      { title: 'Unit & occupancy records', description: 'Track every unit, its current status and assigned occupants.' },
      { title: 'Resident & tenant directory', description: 'Keep resident, tenant and household contact records organised.' },
      { title: 'Landlord & ownership records', description: 'Connect property owners and landlords to the correct units.' },
      { title: 'Notices & targeted communication', description: 'Send updates to the entire estate or selected blocks, units and groups.' },
      { title: 'Reports & audit trail', description: 'Review estate activity, record changes and user actions.' }
    ]
  },
  billing: {
    kicker: 'Billing Engine',
    title: 'Complete billing control for every property and unit.',
    description: 'Automate service charges, rent, utilities and special assessments, then issue invoices, reconcile payments, follow up arrears and report from one connected billing engine.',
    features: [
      { title: 'Service charge & rent billing', description: 'Manage estate service charges, rent and other property-related billing.' },
      { title: 'Recurring & unit-based charges', description: 'Automate recurring fees and apply charges by unit, property type or billing period.' },
      { title: 'Water & utility billing', description: 'Generate water and utility charges using meter readings, consumption or fixed rates.' },
      { title: 'Invoices, receipts & statements', description: 'Issue clear invoices, payment receipts and account statements for every unit.' },
      { title: 'Payments, reconciliation & reminders', description: 'Record payments, reconcile balances and send reminders for overdue accounts.' },
      { title: 'Rent rolls, adjustments & reports', description: 'Generate rent rolls, apply billing adjustments and review financial reports.' }
    ]
  },
  security: {
    kicker: 'Security & Access',
    title: 'Controlled access. Complete security visibility.',
    description: 'Replace paper visitor books and verbal instructions with verified access, traceable movement records, digital permissions and structured guard handovers.',
    features: [
      { title: 'Digital visitor register', description: 'Record every visitor, host, visit purpose, entry time and exit time.' },
      { title: 'Visitor identity verification', description: 'Confirm visitor details and approval before granting estate access.' },
      { title: 'Vehicle access records', description: 'Track authorised vehicles, occupants, entry times and exit activity.' },
      { title: 'Exit pass verification', description: 'Verify approved movement of household assets, vehicles and service providers.' },
      { title: 'Guard handovers & shift logs', description: 'Transfer incidents, instructions and pending security actions between shifts.' },
      { title: 'Security incidents & emergency alerts', description: 'Report security threats and send urgent alerts to the relevant teams.' }
    ]
  },
  work: {
    kicker: 'Work Orders',
    title: 'Coordinate maintenance, workers and vendors from one place.',
    description: 'Create work from requests, inspections or incidents, then schedule teams, manage vendors, track attendance and costs, and close every job with supporting evidence.',
    features: [
      { title: 'Work order creation', description: 'Create maintenance work from resident requests, inspections or reported incidents.' },
      { title: 'Assignment, scheduling & priorities', description: 'Assign responsibility, schedule work and set urgency, deadlines and escalation rules.' },
      { title: 'Worker attendance kiosk', description: 'Allow estate staff, workers and contractors to check in and out through the estate kiosk.' },
      { title: 'Vendor & contractor management', description: 'Manage recurring service providers, institutional vendors and one-time contractors.' },
      { title: 'Procurement connection', description: 'Raise material or contractor needs from the work order and carry approved purchasing costs back to the job.' },
      { title: 'Completion evidence & history', description: 'Close work with notes, photos and approvals while preserving the full maintenance record.' }
    ]
  },
  landlord: {
    kicker: 'Landlord Module',
    title: 'Keep landlord informed.',
    description: 'Give owners a consolidated view of their properties, occupancy, authorised tenancy details, accounts, documents and maintenance activity.',
    features: [
      { title: 'Multi-property portfolio', description: 'View all owned units and properties from one landlord account.' },
      { title: 'Occupancy & tenancy information', description: 'See occupancy status and authorised tenant details for each unit.' },
      { title: 'Statements, settlements & recoveries', description: 'Review collections, approved deductions, recoverable costs, owner statements and settlement amounts.' },
      { title: 'Lease records', description: 'Access lease terms, important dates, renewals and tenancy history.' },
      { title: 'Maintenance visibility', description: 'Track reported issues, work progress, costs and completed repairs.' },
      { title: 'Documents & reports', description: 'Access property documents, financial records and portfolio reports.' }
    ]
  },
  agent: {
    kicker: 'My Agent',
    title: 'Run the management business behind every property.',
    description: 'Manage client portfolios, agreements, management fees, business income and operating expenses separately from estate and property accounts.',
    features: [
      { title: 'Client property portfolio', description: 'Manage every client, property and assigned unit from one consolidated portfolio.' },
      { title: 'Management agreements & fees', description: 'Record management terms and configure fixed, recurring or percentage-based fees.' },
      { title: 'Management-fee invoicing', description: 'Generate and track invoices for fees owed to the property management business.' },
      { title: 'Business income & expenses', description: 'Track company earnings and operating costs separately from client and estate accounts.' },
      { title: 'Client & property profitability', description: 'Measure revenue, costs and margins by client or managed property.' },
      { title: 'Consolidated business reporting', description: 'Review fees, income, expenses and portfolio performance across the management business.' }
    ]
  },
  documents: {
    kicker: 'Documents',
    title: 'Every document connected to the record it belongs to.',
    description: 'Organise property, tenancy, ownership, vendor and compliance documents with controlled access, renewal tracking and a complete history of every change.',
    features: [
      { title: 'Record-linked filing', description: 'Attach documents to the correct estate, property, unit, owner, tenant, vendor or work order.' },
      { title: 'Document access controls', description: 'Control who can view, upload, edit, approve, download or remove each document.' },
      { title: 'Secure sharing & approvals', description: 'Share selected files and manage document review or approval without exposing unrelated records.' },
      { title: 'Expiry & renewal tracking', description: 'Monitor document validity and receive reminders before contracts, policies or licences expire.' },
      { title: 'Search & smart retrieval', description: 'Find documents using property, document type, related record, date, status or keyword.' },
      { title: 'Version & activity history', description: 'Track replacements, approvals, downloads, edits and other document activity over time.' }
    ]
  },
  accounting: {
    kicker: 'Ledgers & Accounting',
    title: 'A complete financial picture for every estate and portfolio.',
    description: 'Track money movement, receivables, payables, funds, budgets and reporting from connected estate ledgers with clear controls and a dependable audit trail.',
    features: [
      { title: 'Journals & general ledger', description: 'Post balanced journals against the correct estate, fund, account, source document and reporting period.' },
      { title: 'AR & AP subledgers', description: 'Track resident and tenant receivables separately from approved supplier and contractor payables, then reconcile both to the general ledger.' },
      { title: 'Funds & cash visibility', description: 'Track operating, reserve and designated funds while keeping balances and money movement visible.' },
      { title: 'Budgets & cost control', description: 'Set estate budgets, compare actual performance and identify areas that need attention.' },
      { title: 'Reconciliation & approvals', description: 'Reconcile bank, billing, receivable and payable balances, resolve exceptions and route adjustments through controlled approvals.' },
      { title: 'Statements & financial reports', description: 'Produce resident, landlord, supplier and estate statements alongside trial balance and financial reports.' }
    ]
  },
  procurement: {
    kicker: 'Procurement',
    title: 'Control purchasing from request to supplier payment.',
    description: 'Move every purchase through request, quotation, order, receipt, GRNI, supplier invoice, accounts payable and payment—with the estate, work order and fund attached throughout.',
    features: [
      { title: 'Purchase requests & approvals', description: 'Capture what is needed, assign the estate or work order, check budget availability and route the request to authorised approvers.' },
      { title: 'RFQs & quote comparison', description: 'Invite supplier quotations, compare price and commercial terms, and retain the decision record.' },
      { title: 'Purchase orders', description: 'Issue approved orders with quantities, pricing, delivery requirements, responsibility and cost allocation.' },
      { title: 'Receipts & GRNI', description: 'Record goods or services received, track partial deliveries and recognise received items that have not yet been invoiced.' },
      { title: 'Supplier invoices & AP', description: 'Match invoices to orders and receipts, resolve exceptions and create the verified supplier payable.' },
      { title: 'Payment & reporting', description: 'Approve settlement, record payment, clear the payable and report spend by supplier, estate, work order and fund.' }
    ]
  },
  payroll: {
    kicker: 'Payroll',
    title: 'Run estate payroll with every employee cost accounted for.',
    description: 'Manage permanent and casual employees, compensation, attendance inputs, payroll runs and payments from a controlled estate payroll workspace.',
    features: [
      { title: 'Employee records', description: 'Maintain employment, role, contract, compensation and banking information for estate staff.' },
      { title: 'Compensation setup', description: 'Configure salaries, wages, allowances, deductions and other employee-specific payroll terms.' },
      { title: 'Attendance & payroll inputs', description: 'Bring approved attendance, shifts, overtime and casual work into payroll preparation.' },
      { title: 'Payroll runs & approvals', description: 'Prepare, review and approve payroll for the correct estate, team and pay period.' },
      { title: 'Payslips & payment records', description: 'Give employees clear pay details while retaining a traceable payment history.' },
      { title: 'Payroll costs & reporting', description: 'Allocate staff costs accurately and review payroll totals across estates and periods.' }
    ]
  },
  assets: {
    kicker: 'Asset Maintenance',
    title: 'Protect every estate asset through its full service life.',
    description: 'Register equipment and infrastructure, plan preventive maintenance, coordinate inspections and connect every repair to its costs, history and supporting work order.',
    features: [
      { title: 'Asset register', description: 'Maintain a structured record of estate equipment, infrastructure, locations, custodians and condition.' },
      { title: 'Preventive maintenance', description: 'Schedule recurring service by date, meter reading, usage or manufacturer recommendation.' },
      { title: 'Inspections & condition tracking', description: 'Capture inspection results, readings, defects and condition changes before failures escalate.' },
      { title: 'Work-order connection', description: 'Create and track corrective work while preserving the relationship between each job and asset.' },
      { title: 'Warranties & service contracts', description: 'Track warranty cover, service providers, contract terms and important renewal dates.' },
      { title: 'Lifecycle cost & service history', description: 'Review maintenance spend, downtime, completed work and the full history of every asset.' }
    ]
  },
  marketplace: {
    kicker: 'Garde Market',
    title: 'Verified property discovery connected to real availability.',
    description: 'Publish authorised listings from real property records, manage buyer and tenant conversations, and carry applications into the next stage without losing context.',
    features: [
      { title: 'Verified listings', description: 'Publish available units from a known property record with an authorised landlord, agent or developer behind each listing.' },
      { title: 'Live availability', description: 'Keep availability aligned with the unit record so prospects do not waste time on stale or duplicate listings.' },
      { title: 'Structured enquiries', description: 'Capture what a prospect is looking for, the property they selected and the next action required.' },
      { title: 'Conversations', description: 'Keep property-specific questions, replies and follow-ups together instead of scattering them across personal chats.' },
      { title: 'Applications & interest', description: 'Receive rental applications, purchase interest and supporting details against the correct listing.' },
      { title: 'Connected onboarding', description: 'Carry an approved applicant into tenancy or ownership setup with the property context already attached.' }
    ]
  }
};

function LandingPage() {
  useEffect(() => {
    const tabs = [...document.querySelectorAll('.module-tab')];
    const moduleSelect = document.getElementById('moduleSelect');
    const moduleSelectWrap = document.getElementById('moduleSelectWrap');
    const moduleSelectMenu = document.getElementById('moduleSelectMenu');
    const moduleSelectValue = document.getElementById('moduleSelectValue');
    const moduleOptions = [...document.querySelectorAll('.module-select-option')];
    const closeModuleSelect = () => {
      moduleSelect.setAttribute('aria-expanded', 'false');
      moduleSelectMenu.hidden = true;
    };
    const renderFeature = (item, index, moduleKey) => {
      if (typeof item === 'string') return `<div class="feature">${item}</div>`;
      const descriptionId = `${moduleKey}-feature-description-${index + 1}`;
      return `<button class="feature feature-interactive" type="button" aria-expanded="false" aria-controls="${descriptionId}"><span class="feature-content"><span class="feature-title">${item.title}</span><span class="feature-description" id="${descriptionId}">${item.description}</span></span></button>`;
    };
    const selectModule = (tab) => {
      tabs.forEach((item) => item.setAttribute('aria-selected', 'false'));
      tab.setAttribute('aria-selected', 'true');
      const selectedOption = moduleOptions.find((item) => item.dataset.module === tab.dataset.module);
      moduleOptions.forEach((item) => item.setAttribute('aria-selected', String(item === selectedOption)));
      if (selectedOption) moduleSelectValue.textContent = selectedOption.textContent;
      const data = modules[tab.dataset.module];
      const elements = ['moduleKicker', 'moduleTitle', 'moduleDescription', 'moduleFeatures'].map((id) => document.getElementById(id));
      elements.forEach((element) => element?.animate(
        [{ opacity: 0.2, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
        { duration: 240, easing: 'ease-out' }
      ));
      document.getElementById('moduleKicker').textContent = data.kicker;
      document.getElementById('moduleTitle').textContent = data.title;
      document.getElementById('moduleDescription').textContent = data.description;
      document.getElementById('moduleFeatures').innerHTML = data.features.map((feature, index) => renderFeature(feature, index, tab.dataset.module)).join('');
      const modulePage = modulePages.find((page) => page.key === tab.dataset.module);
      const moduleLink = document.getElementById('moduleLink');
      if (modulePage && moduleLink) {
        moduleLink.href = `/modules/${modulePage.slug}`;
        moduleLink.textContent = `Explore ${modulePage.name} →`;
      }
    };
    const listeners = tabs.map((tab) => {
      const listener = () => selectModule(tab);
      tab.addEventListener('click', listener);
      return [tab, listener];
    });
    const selectListener = () => {
      const willOpen = moduleSelect.getAttribute('aria-expanded') !== 'true';
      moduleSelect.setAttribute('aria-expanded', String(willOpen));
      moduleSelectMenu.hidden = !willOpen;
    };
    const optionListeners = moduleOptions.map((option) => {
      const listener = () => {
        const matchingTab = tabs.find((tab) => tab.dataset.module === option.dataset.module);
        if (matchingTab) selectModule(matchingTab);
        closeModuleSelect();
        moduleSelect.focus();
      };
      option.addEventListener('click', listener);
      return [option, listener];
    });
    const outsideSelectListener = (event) => {
      if (!moduleSelectWrap.contains(event.target)) closeModuleSelect();
    };
    const selectKeyListener = (event) => {
      if (event.key === 'Escape') closeModuleSelect();
    };
    moduleSelect.addEventListener('click', selectListener);
    document.addEventListener('click', outsideSelectListener);
    document.addEventListener('keydown', selectKeyListener);
    const featureList = document.getElementById('moduleFeatures');
    const toggleFeature = (event) => {
      const selected = event.target.closest('.feature-interactive');
      if (!selected || !featureList.contains(selected)) return;
      const willOpen = selected.getAttribute('aria-expanded') !== 'true';
      featureList.querySelectorAll('.feature-interactive').forEach((feature) => {
        feature.setAttribute('aria-expanded', 'false');
      });
      selected.setAttribute('aria-expanded', String(willOpen));
    };
    featureList.addEventListener('click', toggleFeature);

    const revealElements = document.querySelectorAll('.reveal');
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.1 });
      revealElements.forEach((element) => observer.observe(element));
    } else {
      revealElements.forEach((element) => element.classList.add('visible'));
    }
    document.getElementById('year').textContent = new Date().getFullYear();

    const enquiryLinks = [...document.querySelectorAll('[data-enquiry]')];
    const setEnquiry = (event) => {
      document.getElementById('contact-subject').value = event.currentTarget.dataset.enquiry;
    };
    enquiryLinks.forEach((link) => link.addEventListener('click', setEnquiry));

    const contactForm = document.getElementById('contact-form');
    const submitContact = async (event) => {
      event.preventDefault();
      const formData = new FormData(contactForm);
      const status = document.getElementById('form-status');
      const button = contactForm.querySelector('button[type="submit"]');
      button.disabled = true;
      button.textContent = 'Sending enquiry…';
      status.textContent = '';
      try {
        const response = await fetch('https://garde.co.ke/garde/api/v1/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.fromEntries(formData))
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.message || 'Failed to send enquiry');
        }
        contactForm.reset();
        status.textContent = 'Thank you. Your enquiry has been sent successfully.';
      } catch (error) {
        status.textContent = error.message || 'Failed to send enquiry. Please try again or email info@probt.tech.';
      } finally {
        button.disabled = false;
        button.textContent = 'Send enquiry ↗';
      }
    };
    contactForm.addEventListener('submit', submitContact);

    return () => {
      listeners.forEach(([tab, listener]) => tab.removeEventListener('click', listener));
      moduleSelect.removeEventListener('click', selectListener);
      optionListeners.forEach(([option, listener]) => option.removeEventListener('click', listener));
      document.removeEventListener('click', outsideSelectListener);
      document.removeEventListener('keydown', selectKeyListener);
      featureList.removeEventListener('click', toggleFeature);
      enquiryLinks.forEach((link) => link.removeEventListener('click', setEnquiry));
      contactForm.removeEventListener('submit', submitContact);
      observer?.disconnect();
    };
  }, []);

  return <div dangerouslySetInnerHTML={{ __html: pageMarkup }} />;
}

function CanonicalUrl() {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = pathname.replace(/\/$/, '') || '/';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = `https://garde.co.ke${route}`;
    }
    const titles = {
      '/': 'Property & Estate Management Software in Kenya | Garde',
      '/privacy-policy': 'Privacy Policy | Garde',
      '/terms-of-service': 'Terms of Service | Garde',
      '/data-deletion': 'Data Deletion | Garde',
      '/support': 'Support | Garde',
    };
    const descriptions = {
      '/support': 'Get help with the Garde web, iOS and Android apps. Send a support request and our team will reply by email.',
    };
    const modulePage = getModulePageByPath(route);
    document.title = modulePage?.seoTitle || titles[route] || 'Garde';
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.content = modulePage?.metaDescription
        || descriptions[route]
        || 'Garde is property and estate management software for Kenyan estate managers, landlords and managing agents. Connect billing, visitor access, maintenance, resident communication and property records in one platform.';
    }
  }, [pathname]);

  return null;
}

function PageLoader() {
  return (
    <div className="page-loader" role="status" aria-label="Loading">
      <span className="page-loader-mark"><img src="/assets/garde-logo.jpg" alt="" width="56" height="56" /></span>
    </div>
  );
}

// The homepage stays outside <Suspense>: hydrateRoot can't match a Suspense
// boundary against the static markup in public/index.html.
function LazyPage({ page: Page }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
}

function App() {
  // React has replaced the static homepage markup, so the boot loader can go.
  useLayoutEffect(() => {
    document.documentElement.classList.remove('route-pending');
  }, []);

  return (
    <BrowserRouter>
      <CanonicalUrl />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy-policy" element={<LazyPage page={PrivacyPolicy} />} />
        <Route path="/terms-of-service" element={<LazyPage page={TermsOfService} />} />
        <Route path="/data-deletion" element={<LazyPage page={DataDeletion} />} />
        <Route path="/support" element={<LazyPage page={Support} />} />
        <Route path="/modules/:slug" element={<ProductModulePage modules={modules} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
